/**
 * Inline SVG icons, drawn on a 24×24 grid with a 2px stroke. Deliberately the
 * same drawing style as the customer dashboard's set — the two consoles are
 * distinguished by colour and chrome, not by having different-looking glyphs.
 */
const PATHS = {
  overview: "M3 13h6V3H3zM15 21h6V11h-6zM3 21h6v-4H3zM15 7h6V3h-6z",
  servers: [
    "M3 4h18v6H3zM3 14h18v6H3z",
    "M7 7h.01M7 17h.01",
  ],
  orgs: "M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01",
  users:
    "M17 20v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9.5 10a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM22 20v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
  apps: "M12 2l9 5-9 5-9-5 9-5zM3 12l9 5 9-5M3 17l9 5 9-5",
  // The stacked cylinder, which is what every database tool has drawn since
  // the disk pack it depicts stopped existing. Worth keeping for exactly that
  // reason: it is read instantly and nothing else is.
  databases: [
    "M12 3c4.4 0 8 1.3 8 3s-3.6 3-8 3-8-1.3-8-3 3.6-3 8-3z",
    "M20 6v6c0 1.7-3.6 3-8 3s-8-1.3-8-3V6",
    "M20 12v6c0 1.7-3.6 3-8 3s-8-1.3-8-3v-6",
  ],
  deployments: [
    "M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z",
    "M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z",
  ],
  audit: "M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 14l2 2 4-4",
  plus: "M12 5v14M5 12h14",
  check: "M20 6L9 17l-5-5",
  close: "M6 6l12 12M18 6L6 18",
  menu: "M4 7h16M4 12h16M4 17h16",
  chevron: "M9 6l6 6-6 6",
  refresh: "M3 12a9 9 0 1 0 3-6.7M3 4v4h4",
  search: "M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.35-4.35",
  logout: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9",
  "arrow-up-right": "M7 17L17 7M8 7h9v9",
  warning: "M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z",
  shield: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
  /* Private connections, matching the customer dashboard's glyph for the same
     feature — two consoles showing one thing should not draw it differently. */
  lock: "M5 11h14v10H5zM8 11V7a4 4 0 0 1 8 0v4",
  eye: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
  "eye-off":
    "M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22",
  plans: "M3 10h18M7 15h4M3 6h18a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z",
  revenue: "M3 20h18M7 20V10M12 20V4M17 20v-7",
  mail: "M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM22 7l-10 6L2 7",
  archive: "M3 8h18v12H3zM1 4h22v4H1zM10 12h4",
  // A clock face and two hands, for scheduled jobs. Same path the
  // customer dashboard uses, so one product reads as one product.
  clock: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3 2",
  // An "i" in a ring: something to read before acting, not a warning.
  info: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 11v5M12 8h.01",
  // A globe with its meridian, for domains. The same shape the customer
  // dashboard uses, so one product reads as one product.
  globe: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18",
  // A die with its pins, for the model catalogue. Deliberately not a sparkle:
  // every product on the internet uses one for anything touching AI, and the
  // rail is a list of things the platform runs, not a list of trends.
  models: [
    "M9 9h6v6H9z",
    "M4 8h1M4 12h1M4 16h1M19 8h1M19 12h1M19 16h1M8 4v1M12 4v1M16 4v1M8 19v1M12 19v1M16 19v1",
    "M5 5h14v14H5z",
  ],
  // A megaphone, for operator notices. The horn and the handle, plus the strap
  // that keeps it from reading as a plain funnel at 16px.
  announcements: [
    "M3 11v2a1 1 0 0 0 1 1h3l7 4V6l-7 4H4a1 1 0 0 0-1 1z",
    "M7 14v4a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-2",
    "M18 9a4 4 0 0 1 0 6",
  ],
  // A price tag, for the credit campaigns. It shared the Plans glyph until
  // now, and two items in one rail drawn identically is the one thing an icon
  // is there to prevent — the eye picks the row before the word is read.
  promotions: ["M20.6 13.4 12 22l-9-9V3h10l7.6 7.6a2 2 0 0 1 0 2.8z", "M7.5 7.5h.01"],
  /* Free inference. A key rather than a tag or a gift, because a key is
     literally what that page issues — and the rail refuses two destinations
     the same glyph, which `navigation.test.ts` asserts. */
  key: ["M15 7a4 4 0 1 1-3.9 5H8v3H5v3H2v-3.5L10.5 8.5A4 4 0 0 1 15 7z", "M16.5 7.5h.01"],
  // A trend line against axes, for the inference margin. Bars are Revenue's;
  // these two reports sat side by side under the same drawing, so a glance at
  // the rail could not tell the platform's earnings from one product's.
  trend: ["M3 3v18h18", "M7 14l3.5-3.5 3 3L20 7"],
  /*
   * A life ring, for the support queue. Matches the customer dashboard's own
   * support glyph so the two consoles name the same feature with the same mark.
   */
  support: [
    "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z",
    "M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z",
    "M5.6 5.6l3.9 3.9M14.5 14.5l3.9 3.9M18.4 5.6l-3.9 3.9M9.5 14.5l-3.9 3.9",
  ],
  paperclip:
    "M21.4 11.05l-9.2 9.19a5 5 0 0 1-7.07-7.07l9.2-9.19a3.33 3.33 0 0 1 4.71 4.71l-9.19 9.2a1.67 1.67 0 0 1-2.36-2.36l8.49-8.48",
  send: ["M22 2L11 13", "M22 2l-7 20-4-9-9-4 20-7z"],
  trash: "M4 7h16M10 11v6M14 11v6M5 7l1 14h12l1-14M9 7V4h6v3",
} as const;

export type IconName = keyof typeof PATHS;

export function Icon({ name, className = "size-5" }: { name: IconName; className?: string }) {
  const paths = PATHS[name];
  const list: readonly string[] = typeof paths === "string" ? [paths] : paths;

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      {list.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

/**
 * The platform mark in the console's own amber, never the product blue. Same
 * geometry as the customer dashboard's logo so it is recognisably Maxlayer,
 * different colour so it is unmistakably not the customer dashboard.
 */
export function OpsMark({ className = "size-7" }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true" className={className}>
      <defs>
        <linearGradient id="ops-logo" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--color-ops-400)" />
          <stop offset="100%" stopColor="var(--color-ops-600)" />
        </linearGradient>
      </defs>
      <path d="M16 3.5 29 11 16 18.5 3 11z" fill="url(#ops-logo)" />
      <path
        d="M4.5 15.6 16 22.2l11.5-6.6"
        fill="none"
        stroke="var(--color-ops-500)"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.7"
      />
      <path
        d="M4.5 20.6 16 27.2l11.5-6.6"
        fill="none"
        stroke="var(--color-ops-500)"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.35"
      />
    </svg>
  );
}
